import { effect, inject, Injectable, signal } from '@angular/core';
import {
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../schemas/transaction';
import { createItem, readItems, updateItem, aggregate } from '@directus/sdk';
import { directusAuthInstance } from '../libs/directus';
import { HandleErrorsService } from '../utils/services/handle-errors.service';
import { RequestsService } from '../utils/services/requests.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private handleErrorsService = inject(HandleErrorsService);
  private requestsService = inject(RequestsService);
  transactions = signal<Transaction[]>([]);
  loadedTransactions = signal<boolean>(false);
  latestTransaction = signal<Transaction | null>(null);
  latestTransactionEffect = effect(() => {
    const latestTransaction = this.latestTransaction();
    const allTransactions = this.transactions();
    if (latestTransaction) {
      const foundIndex = allTransactions.findIndex(
        (transaction) => transaction.code === latestTransaction.code,
      );
      if (foundIndex > -1) {
        allTransactions[foundIndex] = latestTransaction;
      } else {
        allTransactions.unshift(latestTransaction);
      }
      // this.transactions.set([]);
      this.transactions.set(allTransactions);
    }
    this.latestTransaction.set(null);
  });
  transactionCount = signal<number>(0);
  directusClient = directusAuthInstance;

  today = new Date();
  sevenDaysAgo = new Date(this.today.getTime() - 7 * 24 * 60 * 60 * 1000);
  startOfDay = this.sevenDaysAgo.toISOString().split('T')[0] + 'T00:00:00Z';
  endOfDay = this.today.toISOString().split('T')[0] + 'T23:59:59Z';

  constructor() {}

  async createTransaction(transaction: Transaction) {
    this.loadedTransactions.set(false);
    const { ...data } = transaction;
    try {
      const transactionResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(createItem('transactions', data)),
      );
      await this.getSingleTransaction(transactionResponse);
      this.loadedTransactions.set(true);
      return transactionResponse as Transaction;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async updateTransaction(transaction: Transaction) {
    this.loadedTransactions.set(false);
    const { id, ...data } = transaction;

    try {
      const transactionResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('transactions', transaction.id!, data),
        ),
      );

      await this.getSingleTransaction(transactionResponse);
      this.loadedTransactions.set(true);
      return transactionResponse as Transaction;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async getTotalTransactionCount(): Promise<number> {
    try {
      const transactionCount = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('transactions', {
            aggregate: { count: '*' },
            filter: {
              date_created: {
                _gte: this.startOfDay,
                _lte: this.endOfDay,
              },
            },
          }),

          // readItems('transactions', {
          //   aggregate: { count: '*' },
          //   filter: {
          //     date_created: {
          //       _lte: this.endOfDay,
          //     },
          //   },
          //   groupBy: ['status'],
          // }),
        ),
      );
      const count = transactionCount[0].count;
      this.transactionCount.set(count);
      return count;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return 0;
    }
  }

  async getSingleTransaction(params: Transaction): Promise<Transaction | null> {
    this.loadedTransactions.set(false);
    const { code, id } = params;
    try {
      const transactions = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('transactions', {
            filter: {
              id: {
                _eq: id,
              },
              code: {
                _eq: code,
              },
            },
            fields: ['*.*.*'],
          }),
        ),
      );

      if (transactions.length === 0) {
        this.loadedTransactions.set(true);
        return null;
      }
      this.latestTransaction.set(transactions[0] as Transaction);
      this.loadedTransactions.set(true);
      return transactions[0] as Transaction;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return null;
    }
  }

  async getTransactions(): Promise<Transaction[]> {
    this.loadedTransactions.set(false);
    await this.getTotalTransactionCount();
    try {
      const transactions = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('transactions', {
            fields: ['*.*.*'],
            sort: '-date_created',
            filter: {
              date_created: {
                _gte: this.startOfDay,
                _lte: this.endOfDay,
              },
            },
          }),
        ),
      );

      if (transactions.length === 0) {
        this.loadedTransactions.set(true);
        return [];
      }
      this.transactions.set(transactions as Transaction[]);
      this.loadedTransactions.set(true);
      return transactions as Transaction[];
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async getPaginatedTransactions(
    page: number,
    limit: number,
  ): Promise<Transaction[]> {
    this.loadedTransactions.set(false);
    try {
      const transactions = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('transactions', {
            fields: ['*.*.*'],
            sort: '-date_created',
            limit: 10,
          }),
        ),
      );

      if (transactions.length === 0) {
        this.loadedTransactions.set(true);
        return [];
      }
      this.transactions.set(transactions as Transaction[]);
      this.loadedTransactions.set(true);
      return transactions as Transaction[];
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      return [];
    }
  }

  async generateTransactions(): Promise<Transaction[]> {
    const transactions: Transaction[] = [];
    for (let i = 0; i < 50; i++) {
      transactions.push({
        id: `${i}`,
        amount: (Math.random() * 100000).toFixed(0),
        code: `TRX${i}`,
        label: 'Cash In',
        type: this.getRandomTransactionType(),
        status: this.getRandomTransactionStatus(),
        sender_status: this.getRandomTransactionStatus(),
        receiver_status: this.getRandomTransactionStatus(),
        sender_account: '',
        receiver_account: '',
        sender: '',
        receiver: '',
        date_created: new Date().toISOString(),
        date_updated: new Date().toISOString(),
      });
    }
    return transactions;
  }

  private getRandomTransactionType(): Transaction['type'] {
    const types = Object.values(TransactionType);
    return types[Math.floor(Math.random() * types.length)];
  }

  private getRandomTransactionStatus(): Transaction['status'] {
    const statuses = Object.values(TransactionStatus).filter(
      (status) =>
        status !== TransactionStatus.ALL &&
        status !== TransactionStatus.CANCELED,
    );
    return statuses[Math.floor(Math.random() * statuses.length)];
  }
}
